import {ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';

import {action} from 'redux/helpers';
import types, {IDialogProps} from './types';
import ApiService from '../../../api/ApiService';
import api from '../../../api';
import {dispatchAsync} from 'redux/common';
import i18n from '../../../i18n';
import {IHolidayCalendar} from 'types/HolidayCalendar';
import {DialogCallbacks} from 'types/common';

import {IDiplomatAdminUser} from 'types/DiplomatAdminUser';
import {IAuthenticationPolicies} from 'types/AuthenticationPolicies';
import {IPasswordData} from 'types/PasswordData';

import {isString} from 'lodash';
import {layoutActions} from '.';
import {DialogTypes} from 'common/names';
import {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {ISupportTicketRequest} from 'types/diplomat/SupportTicketRequest';
import {State} from 'redux/reducer';

const testCallbacks = {
   onSuccess: (resp: any, dispatch?: ThunkDispatch<{}, {}, AnyAction>) => {
      console.debug(resp, !!resp, resp === 'false');
      dispatch &&
         dispatch(
            layoutActions.openSubDialog({
               id: DialogTypes.AlertDialog.id,
               title: i18n.t('Test'),
               content: resp ? i18n.t('Test successful.') : i18n.t('Test failed.'),
            })
         );
   },
   onError: (err: any, dispatch?: ThunkDispatch<{}, {}, AnyAction>) => {
      dispatch &&
         dispatch(
            layoutActions.openSubDialog({
               id: DialogTypes.AlertDialog.id,
               title: i18n.t('Test'),
               content: i18n.t('Test failed.'),
            })
         );
   },
};

function closeSubDialog<T>(dialogType: string, result?: string, data?: T) {
   return action(types.CLOSE_SUB_DIALOG, {dialogType, result, data}) as ICloseSubDialogAction<T>;
}
export interface ICloseSubDialogAction<T> {
   type: String;
   payload?: {
      dialogType: string;
      result?: string;
      data?: T;
   };
   callbacks?: FormOnSubmitHelpers<any>;
}

export default {
   loadTreeNav: () => action(types.LOAD_TREE_NAV.SAGA),
   updateTreeState: (nodeData: any) => (dispatch: ThunkDispatch<{}, {}, AnyAction>) =>
      dispatch(action(types.UPDATE_TREE_STATE, nodeData)),
   openDialog: (dialogType: string) => action(types.OPEN_DIALOG, dialogType),
   closeSubDialog,
   openSubDialog: (id: string | IDialogProps) => {
      let options = id as IDialogProps;
      if (isString(id)) {
         options = {
            id,
         };
      }
      return action(types.OPEN_SUB_DIALOG, options);
   },

   loadCalendars: () => dispatchAsync(api.calendars, types.LOAD_CALENDARS),
   addCalendar: (data: IHolidayCalendar, cbs?: DialogCallbacks) =>
      dispatchAsync(api.calendars, types.ADD_CALENDAR, {
         ...cbs,
         getMethod: ApiService.postSecureJson,
         data,
      }),
   deleteCalendar: (id: string, cbs?: DialogCallbacks) =>
      dispatchAsync(`${api.calendars}/${id}`, types.DELETE_CALENDAR, {
         ...cbs,
         getMethod: ApiService.deleteSecureJson,
      }),
   editCalendar: (data: IHolidayCalendar, cbs?: DialogCallbacks) =>
      dispatchAsync(`${api.calendars}/${data.id}`, types.UPDATE_CALENDAR, {
         ...cbs,
         getMethod: ApiService.putSecureJson,
         data,
      }),

   loadUserAdmins: () => dispatchAsync(api.adminUsers, types.LOAD_USER_ADMINS),
   addUserAdmin: (data: IDiplomatAdminUser, cbs?: DialogCallbacks) =>
      dispatchAsync(api.adminUsersCreate, types.ADD_USER_ADMIN, {
         ...cbs,
         getMethod: ApiService.postSecureJson,
         data,
      }),
   deleteUserAdmin: (id: string, cbs?: DialogCallbacks) =>
      dispatchAsync(`${api.adminUsers}/${id}`, types.DELETE_USER_ADMIN, {
         ...cbs,
         getMethod: ApiService.deleteSecureJson,
      }),
   editUserAdmin: (data: IDiplomatAdminUser, cbs?: DialogCallbacks) =>
      dispatchAsync(api.adminUsers, types.UPDATE_USER_ADMIN, {
         ...cbs,
         getMethod: ApiService.postSecureJson,
         data,
      }),

   getAuthPolicies: dispatchAsync(api.authPolicies, types.GET_AUTH_POLICIES),
   postAuthPolicies: (data: IAuthenticationPolicies, cbs?: DialogCallbacks) =>
      dispatchAsync(api.authPolicies, types.POST_AUTH_POLICIES, {
         ...cbs,
         getMethod: ApiService.postSecureJson,
         data,
      }),

   getPasswordData: dispatchAsync(api.passwordData, types.GET_PASSWORD_DATA),
   postPasswordData: (data: IPasswordData, cbs?: DialogCallbacks) =>
      dispatchAsync(api.passwordData, types.POST_PASSWORD_DATA, {
         ...cbs,
         getMethod: ApiService.postSecureJson,
         data,
      }),

   testSqlAuditDbConnection: (address: string, port: number, timeout: number) =>
      dispatchAsync(api.serverTest, types.TEST_SQL_AUDIT_DB_CONNECTION, {
         getMethod: ApiService.postSecureJson,
         data: JSON.stringify({address, port, timeout}),
         ...testCallbacks,
      }),

   suspendTransactionFolder: () =>
      dispatchAsync(api.suspendTransactionFolder, types.SUSPEND_TRANSACTION_FOLDER, {
         getMethod: ApiService.postSecureJson,
      }),

   getArchiveUsed: dispatchAsync(api.archiveUsed, types.GET_ARCHIVE_USED),
   getArchiveDateRange: dispatchAsync(api.archiveDateRange, types.GET_ARCHIVE_DATE_RANGE),

   auditArchiveNow: () =>
      dispatchAsync(api.auditArchiveNow, types.AUDIT_ARCHIVE_NOW, {
         getMethod: ApiService.postSecureJson,
      }),
   getNumJobHistories: () => dispatchAsync(api.numJobHistories, types.GET_NUM_JOB_HISTORIES),

   setActiveDialogStatus: (textLines: string[], closeButtonLabel?: string) =>
      action(types.SET_ACTIVE_DIALOG_STATUS, {
         textLines: [...textLines],
         closeButtonLabel,
      }),
   clearActiveDialogStatus: () => action(types.CLEAR_ACTIVE_DIALOG_STATUS),

   sendSupportTicket: (data: ISupportTicketRequest) => action(types.SEND_SUPPORT_TICKET.SAGA, data),

   togglePanel: (name: string) => action(types.TOGGLE_PANEL, name),
   toggleAllPanels: (state: boolean) => action(types.TOGGLE_ALL_PANELS, state),

   filterTreeNav: (payload: State['layout']['treeFilter']) => action(types.TREE_SEARCH, payload),

   changeJobMonitorColumnsOrder: (payload: {targetIndex: number; oldIndex: number}) =>
      action(types.CHANGE_JOB_MONITOR_COLUMNS_ORDER, payload),
};
